import {inject, injectable} from 'inversify';

import type {BNCouponApiModel} from '../../../../api/types/bnrks/models';
import type {CoreCouponApiModel} from '../../../../api/types/core/models';
import type {IApiDataMapper} from '../../../../core/ports/types';
import type {Combine} from '../../../../utils/utility-types';

import {DateTimeMapperJrpc} from '../../Date/mappers/DateTime.mapper.jrpc';
import {OfferMapperJrpc} from '../../Offer/mappers/Offer.mapper.jrpc';
import {SubtenantMapperJrpc} from '../../Subtenant/mappers/Subtenant.mapper.jrpc';
import {UserMapperJrpc} from '../../User/mappers/User.mapper.jrpc';
import {CouponFactory} from '../Coupon.factory';
import {CouponModel} from '../Coupon.model';
import {CouponExpandJrpc} from './Coupon.expand.jrpc';

@injectable()
export class CouponMapperJrpc
  implements
    IApiDataMapper<CouponMapperJrpc.ApiData, CouponModel.Props, CouponModel>
{
  constructor(
    @inject(CouponFactory)
    private readonly factory: CouponFactory,
    @inject(CouponExpandJrpc)
    public readonly expand: CouponExpandJrpc,
    @inject(DateTimeMapperJrpc)
    private readonly dateTimeMapper: DateTimeMapperJrpc,
    @inject(UserMapperJrpc)
    private readonly userMapper: UserMapperJrpc,
    @inject(OfferMapperJrpc)
    private readonly offerMapper: OfferMapperJrpc,
    @inject(SubtenantMapperJrpc)
    private readonly subtenantMapper: SubtenantMapperJrpc,
  ) {}

  mapDataToModel(apiData: CouponMapperJrpc.ExpandedApiData): CouponModel {
    return this.factory.create(this.mapDataToProps(apiData));
  }

  mapDataToProps(apiData: CouponMapperJrpc.ExpandedApiData): CouponModel.Props {
    return {
      code: apiData.code,
      createdDate: this.dateTimeMapper.mapToModel(apiData.created),
      description: apiData.description,
      employer: this.employer(apiData),
      expirationDate: this.dateTimeMapper.mapNullableToModel(apiData.expires),
      expiredDate: this.dateTimeMapper.mapNullableToModel(apiData.expired),
      id: apiData._id,
      maxQty: apiData.max_qty,
      offer: this.offer(apiData),
      optionOffers: this.optionOffers(apiData),
      role: this.role(apiData),
      subtenant: this.subtenant(apiData),
      title: apiData.title,
      type: this.type(apiData),
      updatedDate: this.dateTimeMapper.mapToModel(apiData.updated),
      usedQty: apiData.used_qty,
      value: apiData.value,
    };
  }

  private employer(
    apiData: CouponMapperJrpc.ExpandedApiData,
  ): CouponModel.Props['employer'] {
    return apiData.employer
      ? this.userMapper.mapDataToModel(apiData.employer)
      : null;
  }

  private offer(
    apiData: CouponMapperJrpc.ExpandedApiData,
  ): CouponModel.Props['offer'] {
    return apiData.resource
      ? this.offerMapper.mapDataToModel(apiData.resource)
      : null;
  }

  private optionOffers(
    apiData: CouponMapperJrpc.ExpandedApiData,
  ): CouponModel.Props['optionOffers'] {
    return apiData.option_resources
      .filter(Boolean)
      .map(data => this.offerMapper.mapDataToModel(data));
  }

  private role(
    apiData: CouponMapperJrpc.ExpandedApiData,
  ): CouponModel.Props['role'] {
    switch (apiData.role) {
      case 'client':
        return CouponModel.Role.CLIENT;
      case 'coach':
        return CouponModel.Role.COACH;
      default:
        return null;
    }
  }

  private subtenant(
    apiData: CouponMapperJrpc.ExpandedApiData,
  ): CouponModel.Props['subtenant'] {
    return apiData.subtenant
      ? this.subtenantMapper.mapExpandedToModel(apiData.subtenant)
      : null;
  }

  private type(
    apiData: CouponMapperJrpc.ExpandedApiData,
  ): CouponModel.Props['type'] {
    switch (apiData.type) {
      case 'employee':
        return CouponModel.Type.EMPLOYEE;
      case 'employer':
        return CouponModel.Type.EMPLOYER;
      case 'subscription':
        return CouponModel.Type.SUBSCRIPTION;
      default:
        return CouponModel.Type.POINTS;
    }
  }
}

export namespace CouponMapperJrpc {
  export type ApiData = Combine<CoreCouponApiModel, BNCouponApiModel>;

  export type ExpandedApiData = ApiData & CouponExpandJrpc.ExpandedFields;
}
